import type { paths } from "@platform/api-spec";
import { handleApiError } from "@platform/shared/errors";
import createClient, { Middleware } from "openapi-fetch";
import config from "~/config";
import { getImpersonationState } from "~/shared/components/PrivateAppLayout/components/Impersonation";
import { authenticationTokenRefresh } from "~/shared/lib/cognito/helpers/authentication-token-refresh";

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    try {
      const token = await authenticationTokenRefresh();
      if (token) {
        request.headers.set("Authorization", `Bearer ${token}`);
      }

      const impersonationState = getImpersonationState();

      if (impersonationState != null) {
        request.headers.set("X-BL-Impersonate", impersonationState.username);
      }

      return request;
    } catch (err) {
      return request;
    }
  },
};

const apiErrorMiddleware: Middleware = {
  async onResponse({ response }) {
    // For non-OK responses (4xx, 5xx), throw a wrapped ApiError
    if (!response.ok) {
      const clonedResponse = response.clone();
      let errorBody;
      try {
        errorBody = await clonedResponse.json();
      } catch {
        errorBody = await clonedResponse.text();
      }

      // Create an error object that matches what handleApiError expects
      const error = {
        status: response.status,
        response: {
          data: errorBody,
          headers: Object.fromEntries(response.headers.entries()),
        },
      };

      // handleApiError returns a rejected promise, extract and throw the actual error
      try {
        await handleApiError(error);
      } catch (apiError) {
        throw apiError;
      }
    }
  },
};

export const client = createClient<paths>({
  baseUrl: config.api,
  headers: {
    "Content-Type": "application/json",
  },
});
client.use(authMiddleware, apiErrorMiddleware);
